import React, {
  useEffect,
  useState,
  createContext,
  PropsWithChildren
} from 'react';
import getSizes, { ScreenSize } from './getSizes';

import { throttle } from '@carvana/purchase-utils';
import {
  screenResize,
  screenOrientation,
  browserType
} from '@carvana/purchase-store';
import { useDispatch } from 'react-redux';
import { detect } from 'detect-browser';

export interface ContextProps {
  browserType: { name: string; version: string; os: string };
  screenSize: ScreenSize;
}

const UIContext = createContext({
  screenSize: getSizes(window.screen.height, window.screen.width),
  browserType: detect()
});

export default UIContext;

export const UIProvider: React.FC<PropsWithChildren<any>> = ({ children }) => {
  const [size, setSize] = useState(
    getSizes(window.screen.height, window.screen.width)
  );
  const dispatch = useDispatch();
  const browser = detect();

  const uiState = {
    screenSize: size,
    browserType: browser
  };

  dispatch(browserType(browser));
  dispatch(screenResize({ screenSize: size }));

  useEffect(() => {
    const resizeListener = () => {
      let timeout: number;
      const throttleScreenResize = throttle(() => {
        if (timeout) {
          window.cancelAnimationFrame(timeout);
        }

        timeout = window.requestAnimationFrame(() => {
          const newSize = getSizes(window.screen.height, window.screen.width);
          setSize(newSize);
          dispatch(screenResize({ screenSize: newSize }));
        });
      }, 50);

      window.addEventListener('resize', throttleScreenResize);

      return () => {
        window.removeEventListener('resize', throttleScreenResize);
      };
    };

    resizeListener();
  }, []);

  useEffect(() => {
    const orientationListener = () => {
      let orientationTimeout: number;
      const throttleOrientation = () => {
        if (orientationTimeout) {
          window.cancelAnimationFrame(orientationTimeout);
        }

        orientationTimeout = window.requestAnimationFrame(() => {
          const newSize = getSizes(window.screen.height, window.screen.width);
          setSize(newSize);
          dispatch(screenOrientation({ screenSize: newSize }));
          window.scrollTo(0, 0);
        });
      };
      window.addEventListener('orientationchange', throttleOrientation, false);
      return () => {
        window.removeEventListener('orientationchange', throttleOrientation);
      };
    };

    orientationListener();
  }, []);

  return <UIContext.Provider value={uiState}>{children}</UIContext.Provider>;
};
